import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";

export default function PendingApproval() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  // Add this near the top of Dashboard.jsx (before the main auth check)
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const urlUid = params.get("uid");

  if (urlUid) {
    localStorage.setItem("userUid", urlUid);

    // Clean the URL so ?uid=... disappears (better UX & prevents loops)
    window.history.replaceState({}, '', window.location.pathname);
  }
}, []);

  useEffect(() => {
    const verifyAccess = async () => {
      const loginid = localStorage.getItem('currentDerivLogin');

      if (!loginid) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", loginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          localStorage.removeItem('currentDerivLogin');
          navigate("/login", { replace: true });
          return;
        }

        const userDoc = querySnapshot.docs[0];
        const data = userDoc.data();
        const { approved } = data;

        if (approved) {
          navigate("/user", { replace: true });
          return;
        }

        // Pending logic: has loginid but not approved
        if (data.status === "pending" || !approved) {
          setAuthorized(true);
        } else {
          // No Deriv link at all → back to login
          navigate("/login", { replace: true });
        }
      } catch (err) {
        console.error("[PendingApproval] Auth check failed:", err);
        localStorage.removeItem('currentDerivLogin');
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyAccess();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#030712] via-[#050d1a] to-[#030712] flex items-center justify-center text-gray-400">
        <div className="flex flex-col items-center gap-5">
          <div className="w-14 h-14 rounded-full border-4 border-cyan-800/40 border-t-cyan-400 animate-spin"></div>
          <p className="text-lg">Verifying your status...</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#030712] via-[#050d1a] to-[#030712] flex items-center justify-center text-gray-300">
        <div className="text-center max-w-md px-6">
          <h2 className="text-2xl font-bold text-cyan-300 mb-4">Access Restricted</h2>
          <p className="mb-8">You don't have permission to view this page.</p>
          <Link
            to="/login"
            className="inline-block bg-cyan-600 hover:bg-cyan-500 px-8 py-3.5 rounded-full font-semibold transition shadow-lg"
          >
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  // ── PENDING PAGE CONTENT ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#030712] via-[#050d1a] to-[#030712] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_25%,#0891b2_0%,transparent_60%)] animate-pulse-slow"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-10 sm:py-16">
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Matches Blooper AI" className="h-8 sm:h-10 w-auto" />
            <span className="text-lg sm:text-xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-cyan-600">
              Matches Blooper AI
            </span>
          </Link>
          <Link to="/" className="text-cyan-400 hover:text-cyan-300 text-sm sm:text-base transition font-medium">
            ← Home
          </Link>
        </div>

        {/* Mobile payment card */}
        <div className="lg:hidden mb-10">
          <div className="bg-gradient-to-br from-[#0a1325] to-[#050814] rounded-3xl border border-cyan-800/40 p-7 sm:p-9 shadow-2xl shadow-cyan-950/30 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-3 h-3 rounded-full bg-cyan-500 animate-pulse"></div>
              <h2 className="text-2xl font-bold text-cyan-300">Ready to Unlock</h2>
            </div>
            <div className="text-center mb-7">
              <p className="text-6xl sm:text-7xl font-black text-cyan-400 tracking-tight">$300</p>
              <p className="text-lg text-gray-400 mt-1">One-time • Lifetime Matches Access</p>
            </div>
            <div className="bg-[#030712]/70 p-5 rounded-2xl border border-cyan-900/40 font-mono text-sm text-cyan-300 break-all mb-6 text-center">
              TV77MLSSfC8wK4uiwWCj1bHY8JMsWwpWQd
            </div>
            <p className="text-sm text-gray-300 mb-4 text-center">
              Send USDT (TRC20) then verify instantly
            </p>
            <a
              href="https://wa.me/254716691630?text=Hi%2C%20I%20just%20sent%20the%20%24300%20for%20Matches%20Blooper%20AI%20access"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 py-4 rounded-full font-bold text-center text-lg shadow-lg shadow-cyan-900/40 transition mb-4"
            >
              I Sent Payment – Verify Now
            </a>
            <p className="text-xs text-gray-500 text-center">
              Access activated within minutes after confirmation
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* LEFT – Status & Benefits */}
          <div className="space-y-8 lg:space-y-10 order-2 lg:order-1">
            <div>
              <h1 className="text-3xl sm:text-4xl font-black leading-tight">
                Approval in Progress
              </h1>
              <p className="mt-4 text-gray-300 text-base sm:text-lg max-w-xl">
                Your Deriv account is now connected. One final step: complete the payment below to unlock rare high-probability Matches signals.
              </p>
            </div>

            {/* Timeline */}
            <div className="space-y-5">
              {[
                { step: "01", text: "Deriv account linked", active: true },
                { step: "02", text: "Payment confirmation pending", active: false },
                { step: "03", text: "Quick admin review", active: false },
                { step: "04", text: "Instant full access", active: false },
              ].map((item) => (
                <div
                  key={item.step}
                  className={`flex items-center gap-4 p-5 rounded-2xl border transition-all ${
                    item.active
                      ? "border-cyan-600/60 bg-cyan-950/20 backdrop-blur-sm"
                      : "border-cyan-900/40 bg-[#0a1325]/50"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shrink-0 ${
                      item.active ? "bg-cyan-600 text-white" : "bg-cyan-900/40 text-cyan-400"
                    }`}
                  >
                    {item.step}
                  </div>
                  <p className={`text-base ${item.active ? "text-cyan-200 font-medium" : "text-gray-300"}`}>
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="bg-[#0a1325]/60 backdrop-blur-sm p-7 rounded-3xl border border-cyan-900/40">
              <h3 className="text-xl font-bold text-cyan-300 mb-5">
                What You Get After Approval
              </h3>
              <ul className="space-y-4 text-gray-200 text-sm">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 text-xl mt-0.5">✓</span>
                  <span>High-probability Last Digit <strong>Matches</strong> signals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 text-xl mt-0.5">✓</span>
                  <span>Even/Odd signals as backup</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 text-xl mt-0.5">✓</span>
                  <span>All volatility indices (1s optimized)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 text-xl mt-0.5">✓</span>
                  <span>Live pulse timers + confidence scoring</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 text-xl mt-0.5">✓</span>
                  <span>Lifetime updates & priority support (0716691630)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* RIGHT – Desktop Payment Card */}
          <div className="hidden lg:block order-1 lg:order-2">
            <div className="bg-gradient-to-br from-[#0a1325] to-[#050814] rounded-3xl border border-cyan-800/40 p-9 shadow-2xl shadow-cyan-950/40 backdrop-blur-sm sticky top-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 rounded-full bg-cyan-500 animate-pulse"></div>
                <h2 className="text-2xl font-bold text-cyan-300">Unlock Access</h2>
              </div>
              <div className="text-center mb-8">
                <p className="text-6xl font-black text-cyan-400">$300</p>
                <p className="text-lg text-gray-400 mt-1">One-time Lifetime Fee</p>
              </div>
              <div className="bg-[#030712]/70 p-5 rounded-2xl border border-cyan-900/40 font-mono text-sm text-cyan-300 break-all mb-6 text-center">
                TV77MLSSfC8wK4uiwWCj1bHY8JMsWwpWQd
              </div>
              <p className="text-sm text-gray-300 text-center mb-6">
                Send USDT (TRC20) • Contact us after transfer
              </p>
              <a
                href="https://wa.me/254716691630?text=Hi%2C%20I%20just%20sent%20the%20%24300%20for%20Matches%20Blooper%20AI"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 py-4 rounded-full font-bold text-center shadow-lg shadow-cyan-900/40 transition"
              >
                Payment Sent – Verify Now
              </a>
              <p className="text-xs text-gray-500 text-center mt-6">
                Access granted shortly after confirmation
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center text-sm text-gray-400">
          Questions before paying?<br />
          <a
            href="https://wa.me/254716691630"
            className="text-cyan-400 hover:text-cyan-300 transition font-medium"
          >
            WhatsApp / Call: 0716691630
          </a>
        </div>
      </div>

      <footer className="relative mt-16 py-8 text-center text-gray-500 text-sm border-t border-cyan-950/30">
        <p>© 2026 Matches Blooper AI • Trading carries significant risk of loss</p>
      </footer>
    </div>
  );
}